<template>
  <div v-if="!loading" class="page-footer bg-neutral-900/80">
    <div v-if="translation" class="container p-11 pt-16 lg:flex lg:py-28">
      <div class="gap-4 lg:flex lg:basis-1/2">
        <div class="min-w-max font-montserrat text-zinc-100 lg:basis-3/12">
          <h4 class="font-bold">{{ translation.footerOpenHours }}</h4>
          <div class="mt-3">
            <p v-for="hour in openHours" :key="hour.day">
              {{ hour.day + ` ` + hour.openHour }}
            </p>
          </div>
        </div>
        <div class="mt-14 font-montserrat text-zinc-100 lg:mt-0 lg:basis-3/12">
          <h4 class="font-bold">{{ translation.footerNavigation }}</h4>
          <div class="mt-3">
            <router-link
              v-for="link in menuItems"
              :key="link.path"
              :to="link.path"
            >
              <p>{{ link.label }}</p>
            </router-link>
          </div>
        </div>
        <div
          v-if="locationLink"
          class="mt-14 font-montserrat text-zinc-100 lg:mt-0 lg:basis-3/12"
        >
          <h4 class="font-bold">
            <i class="pi pi-map-marker mr-2"></i>{{ translation.footerAddress }}
          </h4>
          <div class="mt-3">
            <a
              :href="locationLink.path"
              target="_blank"
              class="hover:text-amber-400"
            >
              {{ locationLink.label }}
            </a>
          </div>
        </div>
        <div class="mt-14 lg:mt-0 lg:basis-3/12">
          <template v-if="facebookLink || instagramLink">
            <h4 class="font-montserrat font-bold uppercase text-zinc-100">
              {{ translation.footerFollowUs }}
            </h4>
            <div class="mt-3 flex gap-4">
              <a
                v-if="facebookLink"
                :href="facebookLink.path"
                target="_blank"
                class="hover:text-amber-400"
              >
                <i
                  class="icon pi pi-facebook text-2xl text-zinc-100 hover:text-amber-400"
                ></i>
              </a>
              <a
                v-if="instagramLink"
                :href="instagramLink.path"
                target="_blank"
                class="hover:text-amber-400"
              >
                <i
                  class="icon pi pi-instagram text-2xl text-zinc-100 hover:text-amber-400"
                ></i>
              </a>
            </div>
          </template>
          <a
            :href="OIVA_REPORT_URL"
            target="_blank"
            rel="noopener"
            class="mt-6 inline-flex flex-col gap-1 font-montserrat text-sm text-zinc-100 first:mt-0 hover:text-amber-400"
          >
            <img
              src="@/assets/oiva-logo.svg"
              alt=""
              width="98"
              height="32"
              class="h-8 w-auto"
            />
            Oiva-raportti
          </a>
        </div>
      </div>
      <div
        class="mt-14 flex items-end gap-1 lg:mt-0 lg:basis-1/2 lg:justify-end"
      >
        <h1
          class="w-min font-serif text-4xl font-bold leading-none text-yellow-500 sm:w-fit md:text-5xl"
        >
          {{ translation.footerSeeYou }}
        </h1>
        <img src="@/assets/image-5.png" />
      </div>
    </div>
    <div
      class="container border-t border-zinc-100/10 px-11 py-6 font-montserrat text-sm text-zinc-300"
    >
      Verkkosivut:
      <a
        href="https://koodimo.fi"
        target="_blank"
        rel="noopener"
        class="underline underline-offset-2 hover:text-amber-400"
        >Koodimo</a
      >
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";

import axios from "axios";

import { convertToTranslationObject } from "@/utils/convertToTranslationObject";
import { getBrowserLanguage } from "@/utils/languageUtils";

const API_URL = process.env.VUE_APP_API_URL;

// Ruokavirasto's määräys 11/2026 requires a link to the restaurant's latest Oiva report on the home page. This search
// lists every report Ruokavirasto has published for the venue, so the link stays current after each inspection; a
// link to one report's PDF would keep showing that old report. "Turku" narrows the results to this restaurant alone.
const OIVA_REPORT_URL =
  "https://www.oivahymy.fi/yrityshaku?hakusana=Kimin%20Ravintola%20Turku";

const menuItems = ref([]);
const openHours = ref([]);
const translation = ref();
const links = ref([]);
const loading = ref(true);
const error = ref(null);

const facebookLink = computed(() =>
  links.value.find((l) => l.customId === "Facebook")
);
const locationLink = computed(() =>
  links.value.find((l) => l.customId === "Location")
);
const instagramLink = computed(() =>
  links.value.find((l) => l.customId === "Instagram")
);

const fetchLocales = async () => {
  try {
    loading.value = true;
    error.value = null;
    const response = await axios.post(API_URL, {
      query: `
        query($locales: [Locale!]!){
          translations(
            locales: $locales,
            orderBy: component_ASC
            where: {component: "footerLinks"}) {
            navigationItems {
              path
              label
            }
          }
          translationItems(locales: $locales, where: {customId_contains: "footer"}) {
            customId
            text
          }
          openTimes(locales: $locales, orderBy: weekDaysNumber_ASC ) {
            day
            openHour
          }
          links(
            orderBy: customId_ASC
            where: {OR: [{customId: "Facebook"}, {customId: "Location"}, {customId: "Instagram"}]}
            locales: $locales
          ) {
            customId
            label
            path
          }
        }
      `,
      variables: {
        locales: [getBrowserLanguage()],
      },
    });

    if (response.data.errors) {
      console.error("GraphQL errors:", response.data.errors);
    } else {
      menuItems.value = response.data.data.translations[0].navigationItems;
      openHours.value = response.data.data.openTimes;
      links.value = response.data.data.links;
      translation.value = convertToTranslationObject(
        response.data.data.translationItems
      );
    }
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
};

onMounted(fetchLocales);
</script>
<style></style>
